import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import ConsentFormDetails from "../Consent/ConsentForm"; // updated form component
import { createConsentForm } from "../../store/slices/consentSlice"; // your redux thunk
import { fetchPatient } from "../../store/slices/patientsSlice";
import { getApiErrorMessage } from "../../utils/apiError";

const AddConsent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        patientId,
        caseId,
        clientName: "",
        relationToClient: "",
        guardianName: "",
        mrNo: "",
        contact: "",
        address: "",
        date: "",
        amount: "",
        paymentStatus: "",
        referenceNo: "",
        paymentDate: "",
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            clientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                            contact: patient.phone || "",
                            address: patient.address || "",
                            mrNo: patient.mr_no || "",
                        }));
                    }
                })
                .catch((err) => console.error("Failed to fetch patient details:", err));
        }
    }, [dispatch, patientId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let finalValue = value;

        // --- Phone Masking (xxxx-xxxxxxx) ---
        if (name === "contact") {
            const digits = value.replace(/\D/g, "").slice(0, 11);

            if (digits.length <= 4) {
                finalValue = digits;
            } else {
                finalValue = `${digits.slice(0, 4)}-${digits.slice(4)}`;
            }
        }

        setFormData((prev) => ({
            ...prev,
            [name]: finalValue,
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.clientName) newErrors.clientName = "Client name is required";
        if (!formData.relationToClient)
            newErrors.relationToClient = "Relation to client is required";
        if (!formData.guardianName) newErrors.guardianName = "Guardian name is required";
        if (!formData.mrNo) newErrors.mrNo = "MR No. is required";
        if (!formData.contact) newErrors.contact = "Contact is required";
        if (!formData.address) newErrors.address = "Address is required";
        if (!formData.date) newErrors.date = "Date is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill all required fields.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return;
        }

        try {
            // Map camelCase frontend fields to snake_case backend fields
            const payload = {
                patient_id: formData.patientId || null,
                case_id: formData.caseId || null,
                client_name: formData.clientName,
                guardian_name: formData.guardianName,
                relation_to_client: formData.relationToClient,
                mr_no: formData.mrNo,
                contact_no: formData.contact,
                address: formData.address,
                consent_date: formData.date,
                amount: formData.amount,
                payment_status: formData.paymentStatus,
                reference_no: formData.referenceNo,
                payment_date: formData.paymentDate,
            };

            // Dispatch the thunk
            const res = await dispatch(createConsentForm(payload)).unwrap();

            Swal.fire({
                title: "Success!",
                text: "Consent form added successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate(`/consent/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (err) {
            Swal.fire({
                title: "Error",
                text: getApiErrorMessage(err, "Failed to add consent form"),
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.values(errors).map((err, i) => (
                                    <div key={i}>{err}</div>
                                ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <ConsentFormDetails
                                formData={formData}
                                setFormData={setFormData} // pass setter for repeater updates
                                handleChange={handleChange}
                            />

                            <div className="row">
                                <div className="col-md-2 ms-auto">
                                    <button type="submit" className="btn btn-primary w-100 sticky-form-submit">
                                        <i className="ri-save-3-line me-1"></i> Save
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddConsent;
